import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSyncStatus } from "./hooks/useSyncStatus";
import { POLL_STATUS, STAGE_MAP, PIPELINE_STAGES } from "../../constants";
import {
  SyncAnimatedIcon,
  CheckCircleOutlineIcon,
  AlertCircleIcon,
  ErrorCircleFilledIcon,
  InfoIcon,
  RetryIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  RedCrossCircleSmallIcon,
} from "../../icons";
import "./SyncStatus.scss";

function getStageStatus(syncStatus, stageKey) {
  const normalised = (syncStatus || "").toUpperCase();
  const map = STAGE_MAP[normalised] || STAGE_MAP.PROCESSING; // safe fallback
  return map[stageKey] || "pending";
}

// Stage icon
function StageIcon({ status }) {
  if (status === "done") return <span className="ss-stage-icon ss-stage-icon--done">✅</span>;
  if (status === "active") return <span className="ss-stage-icon ss-stage-icon--active">🔄</span>;
  if (status === "failed")
    return <RedCrossCircleSmallIcon className="ss-stage-icon ss-stage-icon--failed" />;
  return <span className="ss-stage-icon ss-stage-icon--pending">⏳</span>;
}

// "Last checked X seconds/minutes ago" display
function useLastCheckedLabel(lastChecked) {
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (!lastChecked) {
      setLabel("");
      return;
    }
    const update = () => {
      const secs = Math.floor((Date.now() - lastChecked.getTime()) / 1000);
      if (secs < 60) setLabel(`${secs}s ago`);
      else setLabel(`${Math.floor(secs / 60)}m ago`);
    };
    update();
    const t = setInterval(update, 5000);
    return () => clearInterval(t);
  }, [lastChecked]);

  return label;
}

// Icons imported from src/icons — no inline SVG definitions here

function SyncStatus({ sessionId, onGoToDownload, onBack, userEmail = "" }) {
  const { pollStatus, pollError, languageStatuses, lastChecked, isChecking, retryPolling } =
    useSyncStatus(sessionId, userEmail);

  const isPolling = pollStatus === POLL_STATUS.POLLING;
  const isComplete = pollStatus === POLL_STATUS.COMPLETE;
  const isError = pollStatus === POLL_STATUS.ERROR;

  const lastCheckedLabel = useLastCheckedLabel(lastChecked);

  useEffect(() => {
    if (!isPolling) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Synchronisation is still in progress. Are you sure you want to leave?";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isPolling]);

  const showGrid = languageStatuses.length > 0;

  return (
    <div className="ss-root" style={{ width: "100%", boxSizing: "border-box" }}>
      {/* ── Error Alert ─────────────────────────────────────────────────── */}
      {isError && (
        <div className="ss-alert ss-alert--error" role="alert">
          <ErrorCircleFilledIcon size={16} />
          <div className="ss-alert-body">
            <strong>Synchronisation failed</strong>
            <span>{pollError}</span>
          </div>
          <button className="ss-alert-retry" onClick={retryPolling} type="button">
            <RetryIcon size={15} /> Retry
          </button>
        </div>
      )}

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="ss-page-header">
        <div
          className={`ss-page-header-icon${isComplete ? " ss-page-header-icon--done" : isError ? " ss-page-header-icon--error" : ""}`}
        >
          {isComplete ? (
            <CheckCircleOutlineIcon size={28} />
          ) : isError ? (
            <AlertCircleIcon size={28} />
          ) : (
            <SyncAnimatedIcon size={28} />
          )}
        </div>
        <div className="ss-page-header-text">
          <h1 className="ss-page-title">
            {isComplete
              ? "Synchronisation Complete"
              : isError
                ? "Synchronisation Failed"
                : "Synchronising…"}
          </h1>
          <p className="ss-page-sub">
            {isComplete
              ? "Your file has been processed successfully."
              : isError
                ? "An error occurred. Please retry or contact your administrator."
                : "Please keep this window open while we process your files."}
          </p>
        </div>
      </div>

      {/* ── Animated / Done Icon (only when no grid yet) ───────────────── */}
      {!isError && !showGrid && (
        <div className={`ss-icon-wrap${isComplete ? " ss-icon-wrap--done" : ""}`}>
          {isComplete ? <CheckCircleOutlineIcon size={28} /> : <SyncAnimatedIcon size={28} />}
        </div>
      )}

      {/* ── Per-Language Progress Grid ───────────────────────────────────── */}
      {showGrid && (
        <div className="ss-lang-card">
          <div className="ss-lang-grid-header">
            <h2 className="ss-card-title">Language Progress</h2>
            <div className="ss-poll-status">
              {isChecking && (
                <span className="ss-checking-indicator">
                  <span className="ss-mini-spinner" />
                  Checking for updates…
                </span>
              )}
              {!isChecking && lastCheckedLabel && (
                <span className="ss-last-checked">Last status fetched : {lastCheckedLabel}</span>
              )}
            </div>
          </div>

          <ul className="ss-lang-grid" aria-label="Per-language sync progress">
            {languageStatuses.map(({ language, sync_status }) => {
              const isFailed = (sync_status || "").toUpperCase() === "FAILED";
              return (
                <li
                  key={language}
                  className={`ss-lang-row${isFailed ? " ss-lang-row--failed" : ""}`}
                >
                  <span className="ss-lang-name">{language}</span>
                  <span className="ss-lang-pipeline">
                    {PIPELINE_STAGES.map((stage, i) => {
                      const status = getStageStatus(sync_status, stage.key);
                      return (
                        <React.Fragment key={stage.key}>
                          {i > 0 && <span className="ss-pipe-arrow">→</span>}
                          <span className={`ss-stage ss-stage--${status}`}>
                            <StageIcon status={status} />
                            <span className="ss-stage-label">{stage.label}</span>
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </span>
                  {isFailed && (
                    <span className="ss-lang-status-badge ss-lang-status-badge--failed">
                      Failed
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {isPolling && !isChecking && (
            <p className="ss-poll-footer">Translation status checks every 1 minute.</p>
          )}
        </div>
      )}

      {/* ── Info Note (polling only, before first poll result) ───────────── */}
      {isPolling && !showGrid && (
        <div className="ss-info-note">
          <InfoIcon size={15} />
          <span>Processing has started. Language progress will appear here shortly.</span>
        </div>
      )}

      {/* ── Footer — Back + Go to Download ───────────────────────────────── */}
      <div className="ss-footer">
        {onBack && (
          <button
            type="button"
            className="ss-back-btn"
            onClick={onBack}
            aria-label="Back to Upload"
          >
            <ArrowLeftIcon size={15} />
            Back
          </button>
        )}
        <button
          type="button"
          className="ss-goto-btn"
          onClick={onGoToDownload}
          disabled={!isComplete}
          aria-label="Go to Download page"
        >
          Go to Download <ArrowRightIcon size={15} />
        </button>
      </div>
    </div>
  );
}

StageIcon.propTypes = {
  status: PropTypes.string,
};

SyncStatus.propTypes = {
  sessionId: PropTypes.string,
  onGoToDownload: PropTypes.func,
  onBack: PropTypes.func,
  userEmail: PropTypes.string,
};

export default SyncStatus;
